import { NAV_GROUPS, type NavGroup, type NavItem } from './navigation';

/** How many destinations get a tab of their own; the fifth slot is "More". */
export const PRIMARY_TAB_COUNT = 4;

/**
 * Roles whose work is out on a property. They get Capture and Map on the tab
 * bar; everyone else gets the desk's tools there and finds the field screens
 * under More.
 */
const FIELD_ROLES = new Set([
  'gov_officer',
  'gov_supervisor',
  'lead_officer',
  'assisting_officer',
  'company_officer',
  'company_supervisor',
]);

/** Preference order for the primary tabs. The first four that are visible win. */
const FIELD_ORDER = ['files', 'publicFiles', 'capture', 'map', 'inbox', 'messages'];
const DESK_ORDER = ['files', 'publicFiles', 'inbox', 'messages', 'frontDesk', 'triage', 'capture'];

export function isFieldRole(roles: readonly string[] | undefined): boolean {
  // No scope yet (or none at all): assume the field. An officer opening the
  // portal on a phone is the case the tab bar is designed around.
  if (!roles || roles.length === 0) return true;
  return roles.some((role) => FIELD_ROLES.has(role));
}

export interface MobileTabs {
  primary: NavItem[];
  /** Everything else, grouped as the sidebar groups it. Empty groups are dropped. */
  more: NavGroup[];
}

/**
 * Split the visible destinations into four tabs and a "More" sheet.
 *
 * Thirteen tabs on a 390px screen gave every label three letters and every
 * target a sliver; four plus More keeps each label whole in both languages.
 */
export function mobileTabs(
  isVisible: (item: NavItem) => boolean,
  field: boolean,
  groups: readonly NavGroup[] = NAV_GROUPS,
): MobileTabs {
  const visible = groups.flatMap((g) => g.items).filter(isVisible);
  const byKey = new Map(visible.map((item) => [item.key, item]));
  const order = field ? FIELD_ORDER : DESK_ORDER;
  const primary: NavItem[] = [];
  for (const key of order) {
    const item = byKey.get(key);
    if (item && primary.length < PRIMARY_TAB_COUNT) primary.push(item);
  }
  // Fewer preferred destinations than slots (a narrow role): fill from the
  // sidebar order so the bar is never emptier than it needs to be.
  for (const item of visible) {
    if (primary.length >= PRIMARY_TAB_COUNT) break;
    if (!primary.includes(item)) primary.push(item);
  }
  const taken = new Set(primary.map((item) => item.key));
  const more = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isVisible(item) && !taken.has(item.key)),
    }))
    .filter((group) => group.items.length > 0);
  return { primary, more };
}
